import React from "react";
import { useSessionContext } from "./SessionContext";
import {
    getConfigByKeyMerged,
    PublisherSetting,
    SocialSetting,
    VariantSetting,
} from "core/services/site/configValue";
import {
    configMultiplePublishsettings,
    PublishSettingsParams,
} from "core/services/site/project";
import { preparePublish, listFragments } from "core/services/site/fragment";

interface FbPage {
    id: string;
    name: string;
    publisherKey?: string;
}
type PublishContextType = {
    accounts: PublisherSetting<SocialSetting>[];
    activeFacebookAccounts: PublisherSetting<SocialSetting>[];
    activeTwitterAccounts: PublisherSetting<SocialSetting>[];
    activeYoutubeAccounts: PublisherSetting<SocialSetting>[];
    facebookPages: FbPage[];
    publishOptions: PublisherSetting<SocialSetting>[];
    publishFormats: VariantSetting[];
    reloadSocialAccounts(): void;
};

const getFacebookPages = async (
    accessToken: string,
    projectId: number,
    publisherKey: string,
): Promise<FbPage[]> => {
    try {
        const projectFragments = await listFragments({
            accessToken,
            projectId,
        });
        if (projectFragments.error || !projectFragments.value) {
            throw new Error("Problem while getting fragments for project");
        }
        const facebookPagesResult = await preparePublish<FbPage[]>(
            accessToken,
            projectFragments.value.fragments[0].id,
            publisherKey,
            "listpages",
        );
        if (facebookPagesResult.error || !facebookPagesResult.value) {
            throw new Error("get FB pages call failed");
        }
        return facebookPagesResult.value.map((page) => ({
            id: page.id,
            name: page.name,
            publisherKey,
        }));
    } catch (err) {
        console.error(err);
        return [];
    }
};

const PublishContext = React.createContext<PublishContextType | null>(null);

const usePublishContext = (): PublishContextType => {
    const context = React.useContext(PublishContext);
    if (!context) {
        throw Error("Unable to create publish context");
    }
    return context;
};

type Props = {
    children: React.ReactNode;
};

const PublishContextProvider: React.FC<Props> = ({ children }) => {
    const sessionContext = useSessionContext();
    const [accounts, setAccounts] = React.useState<
        PublisherSetting<SocialSetting>[]
    >([]);
    const [publishOptions, setPublishOptions] = React.useState<
        PublisherSetting<SocialSetting>[]
    >([]);
    const [publishFormats, setPublishFormats] = React.useState<
        VariantSetting[]
    >([]);
    const [activeTwitterAccounts, setActiveTwitterAccounts] = React.useState<
        PublisherSetting<SocialSetting>[]
    >([]);
    const [activeFacebookAccounts, setActiveFacebookAccounts] = React.useState<
        PublisherSetting<SocialSetting>[]
    >([]);
    const [activeYoutubeAccounts, setActiveYoutubeAccounts] = React.useState<
        PublisherSetting<SocialSetting>[]
    >([]);
    const [facebookPages, setFacebookPages] = React.useState<FbPage[]>([]);

    const getPublishFormats = async () => {
        try {
            if (
                !sessionContext.verified ||
                !sessionContext.session ||
                !sessionContext.selectedProject
            ) {
                throw Error("missing data for getting publish formats");
            }
            const configByKey = await getConfigByKeyMerged<VariantSetting[]>(
                sessionContext.session.token,
                "fragmentvariant_settings",
                sessionContext.selectedProject.id,
            );
            if (configByKey.error || !configByKey.value) {
                throw new Error(
                    configByKey.error ||
                        "Reponse error while getting publish formats",
                );
            }
            const availablePublishFormats = configByKey.value.value;
            setPublishFormats(availablePublishFormats);
        } catch (err) {
            console.error(err);
        }
    };

    const checkAccountsActive = async (
        socialAccounts: PublisherSetting<SocialSetting>[],
    ): Promise<PublisherSetting<SocialSetting>[]> => {
        if (!sessionContext.session || !sessionContext.selectedProject) {
            return [];
        }
        const settingsArr: PublishSettingsParams[] = socialAccounts.map(
            (social) => ({
                publishSettingKey: social.key,
                operation: "isauthenticated",
                parameters: "{}",
            }),
        );
        const checkAccountsAuthenticated = await configMultiplePublishsettings(
            sessionContext.session.token,
            sessionContext.selectedProject.id,
            settingsArr,
        );
        if (
            checkAccountsAuthenticated.error ||
            !checkAccountsAuthenticated.value
        ) {
            throw new Error(checkAccountsAuthenticated.error);
        }
        const filteredSocialAccounts = socialAccounts.filter(
            (socialAccount) =>
                checkAccountsAuthenticated.value?.find(
                    (checkAccount) =>
                        checkAccount.publishSettingKey === socialAccount.key,
                )?.resultValue === "true",
        );
        return filteredSocialAccounts;
    };

    const getSocialAccounts = async (
        sessionToken: string,
        selectedProjectID: number,
    ): Promise<void> => {
        try {
            const accountsResult = await getConfigByKeyMerged<
                PublisherSetting<SocialSetting>[]
            >(sessionToken, "fragmentpublish_settings", selectedProjectID);
            if (accountsResult.error || !accountsResult.value) {
                throw new Error("unable to fetch publisher settings");
            }
            const allAccounts = accountsResult.value.value;
            setAccounts(allAccounts);
            const socialAccounts = allAccounts.filter(
                (publisher) =>
                    publisher.publisherkey === "default_facebook" ||
                    publisher.publisherkey === "default_twitter" ||
                    publisher.publisherkey === "default_youtube",
            );
            const publishAccounts = allAccounts.filter(
                (publisher) =>
                    publisher.publisherkey !== "default_facebook" &&
                    publisher.publisherkey !== "default_twitter" &&
                    publisher.publisherkey !== "default_youtube",
            );
            setPublishOptions(publishAccounts);
            // Check what social accounts are connected and authenticated
            const newActiveFacebookAccounts: PublisherSetting<SocialSetting>[] =
                [];
            const newActiveTwitterAccounts: PublisherSetting<SocialSetting>[] =
                [];
            const newActiveYoutubeAccounts: PublisherSetting<SocialSetting>[] =
                [];

            const activeAccounts = await checkAccountsActive(socialAccounts);

            activeAccounts.forEach((account) => {
                if (account.publisherkey === "default_facebook") {
                    newActiveFacebookAccounts.push(account);
                }
                if (account.publisherkey === "default_twitter") {
                    newActiveTwitterAccounts.push(account);
                }
                if (account.publisherkey === "default_youtube") {
                    newActiveYoutubeAccounts.push(account);
                }
            });

            setActiveFacebookAccounts(newActiveFacebookAccounts);
            setActiveTwitterAccounts(newActiveTwitterAccounts);
            setActiveYoutubeAccounts(newActiveYoutubeAccounts);

            let allFbPages: FbPage[] = [];
            await Promise.all(
                newActiveFacebookAccounts.map(async (account) => {
                    if (!sessionContext.session) {
                        throw Error("no session");
                    }
                    if (!sessionContext.selectedProject) {
                        throw Error("no selected project");
                    }
                    const accountFbPages = await getFacebookPages(
                        sessionContext.session.token,
                        sessionContext.selectedProject.id,
                        account.key,
                    );
                    allFbPages = allFbPages.concat(accountFbPages);
                }),
            );
            setFacebookPages(allFbPages);
        } catch (error) {
            console.error("[PublishContext] error", error);
        }
    };

    React.useEffect(() => {
        if (
            !sessionContext.session?.token ||
            !sessionContext.selectedProject?.id
        ) {
            return;
        }
        getPublishFormats();
        getSocialAccounts(
            sessionContext.session.token,
            sessionContext.selectedProject.id,
        );
    }, [sessionContext.selectedProject?.id, sessionContext.session?.token]);

    React.useEffect(() => {
        if (
            !sessionContext.session?.token ||
            !sessionContext.selectedProject?.id
        ) {
            return;
        }
        getPublishFormats();
    }, []);

    const context: PublishContextType = {
        accounts,
        activeFacebookAccounts,
        activeTwitterAccounts,
        activeYoutubeAccounts,
        facebookPages,
        publishOptions,
        publishFormats,
        reloadSocialAccounts: () => {
            if (!sessionContext.session || !sessionContext.selectedProject) {
                return;
            }
            getSocialAccounts(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
            );
        },
    };

    return (
        <PublishContext.Provider value={context}>
            {children}
        </PublishContext.Provider>
    );
};

export default PublishContext;
export { PublishContextProvider, usePublishContext };
